// src/components/MyProperties.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getMyProperties, deactivateProperty, reactivateProperty } from '../api';

function MyProperties() {
  const navigate = useNavigate();
  const userRole = localStorage.getItem('role');

  const [properties, setProperties] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Filters
  const [filters, setFilters] = useState({
    city: '',
    sub_locality: '',
    property_type: '',
    propertyCategory: '',
    minCarpet: '',
    maxCarpet: '',
    status: '',
    search: '',
    activeOnly: true,
  });

  const [page, setPage] = useState(1);

  useEffect(() => {
    fetchProperties();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const fetchProperties = async () => {
    try {
      setLoading(true);
      setError('');

      // Build query params (skip empty values)
      const params = { page, limit: 20 };
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== '' && value !== null && value !== undefined) {
          params[key] = value;
        }
      });

      const response = await getMyProperties(params);
      setProperties(response.properties || []);
      setPagination(response.pagination || { page: 1, pages: 1, total: 0 });
    } catch (err) {
      const message = err.response?.data?.error || err.message || 'Failed to load properties';
      setError(message);
      if (err.response?.status === 401) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFilters({
      ...filters,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleApplyFilters = () => {
    setPage(1);  // Reset to page 1 when filters change
    fetchProperties();
  };

  const handleClearFilters = () => {
    setFilters({
      city: '',
      sub_locality: '',
      property_type: '',
      propertyCategory: '',
      minCarpet: '',
      maxCarpet: '',
      status: '',
      search: '',
      activeOnly: true,
    });
    setPage(1);
    // Will trigger useEffect via setPage
  };

  const handleDeactivate = async (id, location) => {
    if (!window.confirm(`Mark "${location}" as inactive? You'll choose a reason next.`)) return;

    const reason = prompt('Reason for deactivating?\nType: leased, sold, or withdrawn', 'leased');
    if (!reason || !['leased', 'sold', 'withdrawn'].includes(reason.toLowerCase())) {
      alert('Invalid reason. Please use: leased, sold, or withdrawn');
      return;
    }

    try {
      await deactivateProperty(id, reason.toLowerCase(), '');
      setSuccessMessage(`✅ Marked as ${reason}`);
      setTimeout(() => setSuccessMessage(''), 3000);
      fetchProperties();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to deactivate');
    }
  };

  const handleReactivate = async (id, location) => {
    if (!window.confirm(`Reactivate "${location}"?`)) return;

    try {
      await reactivateProperty(id);
      setSuccessMessage('✅ Property reactivated');
      setTimeout(() => setSuccessMessage(''), 3000);
      fetchProperties();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to reactivate');
    }
  };

  const canEdit = (property) => {
    if (userRole === 'admin' || userRole === 'team_lead') return true;
    if ((userRole === 'rep' || userRole === 'broker') && property.createdBy === property._id /* placeholder - will use real userId */) return true;
    // Real check: property.createdBy?.toString() === localStorage userId (from token)
    return ['admin', 'team_lead', 'rep', 'broker'].includes(userRole);
  };

  const canDeactivate = (property) => {
    if (property.lifecycle !== 'active') return false;
    return ['admin', 'team_lead', 'rep', 'broker'].includes(userRole);
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      changes_requested: 'bg-orange-100 text-orange-800',
    };
    return styles[status] || 'bg-gray-100 text-gray-800';
  };

  const getLifecycleBadge = (lifecycle) => {
    const styles = {
      active: 'bg-blue-100 text-blue-800',
      leased: 'bg-purple-100 text-purple-800',
      sold: 'bg-pink-100 text-pink-800',
      withdrawn: 'bg-gray-100 text-gray-800',
    };
    return styles[lifecycle] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-4 gap-2">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800">📋 My Properties</h1>
          <p className="text-sm text-gray-500 mt-1">
            {userRole === 'admin' && 'All properties across all cities'}
            {userRole === 'team_lead' && 'All properties in your assigned cities'}
            {(userRole === 'rep' || userRole === 'broker') && 'Properties you have submitted'}
          </p>
        </div>
        <div className="text-sm text-gray-600">
          Total: <span className="font-bold">{pagination.total}</span>
        </div>
      </div>

      {/* Success/Error messages */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-2 rounded mb-4">
          {successMessage}
        </div>
      )}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded mb-4">
          {error}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
        <h3 className="font-bold text-gray-700 mb-3">🔍 Filters</h3>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {/* City */}
          <div>
            <label className="block text-xs text-gray-600 mb-1">City</label>
            <select
              name="city"
              value={filters.city}
              onChange={handleFilterChange}
              className="border rounded p-2 w-full text-sm"
            >
              <option value="">All Cities</option>
              <option value="mumbai">Mumbai</option>
              <option value="pune">Pune</option>
              <option value="delhi">Delhi</option>
              <option value="bangalore">Bangalore</option>
            </select>
          </div>

          {/* Micromarket */}
          <div>
            <label className="block text-xs text-gray-600 mb-1">Micromarket</label>
            <input
              type="text"
              name="sub_locality"
              value={filters.sub_locality}
              onChange={handleFilterChange}
              placeholder="e.g., Andheri East"
              className="border rounded p-2 w-full text-sm"
            />
          </div>

          {/* Property Type */}
          <div>
            <label className="block text-xs text-gray-600 mb-1">Type</label>
            <select
              name="propertyCategory"
              value={filters.propertyCategory}
              onChange={handleFilterChange}
              className="border rounded p-2 w-full text-sm"
            >
              <option value="">All Types</option>
              <option value="office">Office</option>
              <option value="warehouse">Warehouse</option>
            </select>
          </div>

          {/* Status */}
          <div>
            <label className="block text-xs text-gray-600 mb-1">Status</label>
            <select
              name="status"
              value={filters.status}
              onChange={handleFilterChange}
              className="border rounded p-2 w-full text-sm"
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="changes_requested">Changes Requested</option>
            </select>
          </div>

          {/* Min Carpet */}
          <div>
            <label className="block text-xs text-gray-600 mb-1">Min Carpet (sqft)</label>
            <input
              type="number"
              name="minCarpet"
              value={filters.minCarpet}
              onChange={handleFilterChange}
              placeholder="e.g., 5000"
              className="border rounded p-2 w-full text-sm"
            />
          </div>

          {/* Max Carpet */}
          <div>
            <label className="block text-xs text-gray-600 mb-1">Max Carpet (sqft)</label>
            <input
              type="number"
              name="maxCarpet"
              value={filters.maxCarpet}
              onChange={handleFilterChange}
              placeholder="e.g., 15000"
              className="border rounded p-2 w-full text-sm"
            />
          </div>

          {/* Search */}
          <div>
            <label className="block text-xs text-gray-600 mb-1">Search</label>
            <input
              type="text"
              name="search"
              value={filters.search}
              onChange={handleFilterChange}
              placeholder="Location, building..."
              className="border rounded p-2 w-full text-sm"
            />
          </div>

          {/* Active Only */}
          <div className="flex items-end">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                name="activeOnly"
                checked={filters.activeOnly}
                onChange={handleFilterChange}
                className="w-4 h-4"
              />
              Active only
            </label>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex gap-2 mt-3">
          <button
            onClick={handleApplyFilters}
            className="bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded text-sm"
          >
            Apply Filters
          </button>
          <button
            onClick={handleClearFilters}
            className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded text-sm"
          >
            Clear
          </button>
        </div>
      </div>

      {/* Property List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      ) : properties.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm p-12 text-center">
          <p className="text-gray-500 text-lg">No properties found</p>
          <p className="text-gray-400 text-sm mt-2">Try adjusting your filters</p>
        </div>
      ) : (
        <div className="space-y-3">
          {properties.map((property) => (
            <div
              key={property._id}
              className="bg-white rounded-lg shadow-sm p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex flex-col md:flex-row md:justify-between gap-3">
                {/* Left: Property Info */}
                <div className="flex-1">
                  <div className="flex items-start gap-2 mb-2">
                    <h3 className="font-bold text-lg text-gray-800">
                      {property.location || 'Untitled'}
                    </h3>
                    <span className={`text-xs px-2 py-1 rounded ${getStatusBadge(property.status)}`}>
                      {property.status}
                    </span>
                    {property.lifecycle !== 'active' && (
                      <span className={`text-xs px-2 py-1 rounded ${getLifecycleBadge(property.lifecycle)}`}>
                        {property.lifecycle}
                      </span>
                    )}
                  </div>

                  <p className="text-sm text-gray-600 mb-1">
                    📍 {property.sub_locality || 'N/A'}, {property.city || 'N/A'}
                  </p>

                  <div className="flex flex-wrap gap-3 text-sm text-gray-700 mt-2">
                    <span>🏢 {property.property_type || 'N/A'}</span>
                    {property.rent_per_sqft && property.rent_per_sqft !== 'N/A' && (
                      <span>💰 {property.rent_per_sqft}</span>
                    )}
                    {property.price && property.price !== 'Price on Request' && (
                      <span>💵 {property.price}</span>
                    )}
                    {property.carpet_area && property.carpet_area !== 'N/A' && (
                      <span>📐 {property.carpet_area}</span>
                    )}
                    {property.floor && property.floor !== 'N/A' && (
                      <span>🏗️ {property.floor}</span>
                    )}
                  </div>

                  {property.createdByEmail && property.createdByEmail !== 'N/A' && (
                    <p className="text-xs text-gray-400 mt-2">
                      Submitted by: {property.createdByEmail}
                    </p>
                  )}
                </div>

                {/* Right: Actions */}
                <div className="flex flex-wrap gap-2 md:flex-col md:items-end">
                    <button
                        onClick={() => navigate(`/property/${property._id}`)}
                        className="text-xs bg-gray-100 hover:bg-gray-200 px-3 py-1.5 rounded"
                    >
                        👁️ View
                    </button>

                  {canEdit(property) && (
                    <button
                      onClick={() => navigate(`/edit-property/${property._id}`)}
                      className="text-xs bg-blue-100 hover:bg-blue-200 text-blue-700 px-3 py-1.5 rounded"
                    >
                      ✏️ Edit
                    </button>
                  )}

                  {canDeactivate(property) && (
                    <button
                      onClick={() => handleDeactivate(property._id, property.location)}
                      className="text-xs bg-orange-100 hover:bg-orange-200 text-orange-700 px-3 py-1.5 rounded"
                    >
                      🔒 Deactivate
                    </button>
                  )}

                  {property.lifecycle !== 'active' && ['admin', 'team_lead'].includes(userRole) && (
                    <button
                      onClick={() => handleReactivate(property._id, property.location)}
                      className="text-xs bg-green-100 hover:bg-green-200 text-green-700 px-3 py-1.5 rounded"
                    >
                      🔓 Reactivate
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex justify-center items-center gap-3 mt-6">
          <button
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page <= 1}
            className="px-4 py-2 bg-white border rounded disabled:opacity-50 hover:bg-gray-50"
          >
            ← Prev
          </button>
          <span className="text-sm text-gray-600">
            Page {pagination.page} of {pagination.pages}
          </span>
          <button
            onClick={() => setPage(Math.min(pagination.pages, page + 1))}
            disabled={page >= pagination.pages}
            className="px-4 py-2 bg-white border rounded disabled:opacity-50 hover:bg-gray-50"
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}

export default MyProperties;